import { NavLink, useLocation } from 'react-router-dom'
import { gameSidebarMenuItems } from '@constants/gameMenu'
import { isSpaRoute } from '@routing/spaRoutes'
import { type MenuItem } from '@app-types/api/auth'
import { useAuthStore } from '@stores/authStore'

const emptyMenuItems: MenuItem[] = []

interface GameMenuSidebarProps {
  isCollapsed: boolean
  onToggleCollapsed: () => void
}

function GameMenuSidebar({
  isCollapsed,
  onToggleCollapsed,
}: GameMenuSidebarProps) {
  const location = useLocation()
  const error = useAuthStore((state) => state.error)
  const isLoading = useAuthStore((state) => state.isLoading)
  const session = useAuthStore((state) => state.session)
  const currentApp = session?.currentApp
  const items = currentApp
    ? gameSidebarMenuItems
        .filter((item) => {
          if (item.feature && !currentApp.features.includes(item.feature)) {
            return false
          }

          if (item.perm && !session.user?.perms.includes(item.perm)) {
            return false
          }

          return true
        })
        .map<MenuItem>((item) => ({
          active: false,
          isHeader: Boolean(item.isHeader),
          title: item.title,
          url: item.url,
          v2: true,
        }))
    : emptyMenuItems
  const currentPath = location.pathname.replace(/\/$/, '') || '/'

  return (
    <aside
      className={`min-h-0 overflow-y-auto border-r border-slate-200 bg-white dark:border-zinc-800 dark:bg-zinc-900 max-[820px]:max-h-80 max-[820px]:border-r-0 max-[820px]:border-b ${
        isCollapsed ? 'px-2 py-3' : 'px-3 py-0'
      }`}
    >
      <div
        className={`sticky top-0 z-10 border-b border-slate-100 bg-white dark:border-zinc-800 dark:bg-zinc-900 ${
          isCollapsed
            ? '-mx-2 -mt-3 mb-2 px-2 py-3'
            : '-mx-3 -mt-4 mb-2 px-3 py-4'
        }`}
      >
        <div
          className={`flex items-center ${
            isCollapsed ? 'justify-center' : 'justify-between gap-2'
          }`}
        >
          {isCollapsed ? null : (
            <h2 className="m-0 text-sm font-bold tracking-normal text-slate-950 dark:text-slate-100">
              Меню
            </h2>
          )}
          <button
            aria-label={isCollapsed ? 'Показать меню' : 'Скрыть меню'}
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-slate-200 bg-white text-lg leading-none font-semibold text-slate-600 transition hover:bg-slate-100 hover:text-slate-950 dark:border-zinc-700 dark:bg-zinc-900 dark:text-slate-300 dark:hover:bg-zinc-800 dark:hover:text-white"
            onClick={onToggleCollapsed}
            title={isCollapsed ? 'Показать меню' : 'Скрыть меню'}
            type="button"
          >
            {isCollapsed ? '›' : '‹'}
          </button>
        </div>
      </div>

      {isCollapsed ? null : (
        <>
          {error ? (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-800 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-200">
              {error}
            </div>
          ) : null}

          {isLoading ? (
            <div className="space-y-2 py-4">
              <div className="h-9 animate-pulse rounded-md bg-slate-100 dark:bg-zinc-800" />
              <div className="h-9 animate-pulse rounded-md bg-slate-100 dark:bg-zinc-800" />
              <div className="h-9 animate-pulse rounded-md bg-slate-100 dark:bg-zinc-800" />
            </div>
          ) : null}

          {!isLoading ? (
            <ul className="m-0 grid list-none gap-1 px-0 py-4">
              {items.map((item, index) => {
                if (item.isHeader) {
                  return (
                    <li
                      className="px-3 pt-4 pb-1 text-[11px] font-bold tracking-wide text-slate-400 uppercase first:pt-1 dark:text-zinc-500"
                      key={`${item.title}-${index}`}
                    >
                      {item.title}
                    </li>
                  )
                }

                const itemPath = item.url.replace(/\/$/, '') || '/'
                const isActive = currentPath === itemPath
                const className = `block min-w-0 truncate rounded-md px-3 py-2 text-sm font-semibold no-underline transition ${
                  isActive
                    ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-950'
                    : 'text-slate-700 hover:bg-slate-100 hover:text-slate-950 dark:text-slate-200 dark:hover:bg-zinc-800 dark:hover:text-white'
                }`

                return (
                  <li key={`${item.title}-${item.url}`}>
                    {isSpaRoute(item.url) ? (
                      <NavLink className={className} to={item.url}>
                        {item.title}
                      </NavLink>
                    ) : (
                      <a className={className} href={item.url}>
                        {item.title}
                      </a>
                    )}
                  </li>
                )
              })}
            </ul>
          ) : null}

          {!isLoading && items.length === 0 ? (
            <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-3 text-sm text-slate-500 dark:border-zinc-800 dark:bg-zinc-950 dark:text-slate-400">
              Пункты меню не найдены
            </div>
          ) : null}
        </>
      )}
    </aside>
  )
}

export default GameMenuSidebar
